import { FC, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { useTrans } from '@libs/i18n';
import { FileIcon, TrashIcon, UploadSimpleIcon, XIcon } from '@phosphor-icons/react';

import {
  useConfirmReferenceUpload,
  useDiscardReferenceUpload,
  useRequestReferenceUploadUrl,
} from '../../../api/tenant-profile-documents-api';
import type { ProfileReferenceFileDto } from '../../../api/types';
import { editBoxSx,ORG_TOKENS } from '../../utils/tokens';

import type { FieldInputProps } from './fieldInput.types';

const GLOBAL_MAX_BYTES = 10 * 1024 * 1024; // 10 MB fallback when catalog omits maxFileSizeBytes

// ── Helpers ───────────────────────────────────────────────────────────────

function docLabel(contentType: string): string {
  if (contentType.includes('pdf')) return 'PDF';
  if (contentType.includes('word') || contentType.includes('msword')) return 'DOC';
  if (contentType.includes('sheet') || contentType.includes('excel')) return 'XLS';
  if (contentType.startsWith('image/')) return 'IMG';
  return 'FILE';
}

const DocBadge: FC<{ contentType: string }> = observer(({ contentType }) => (
  <Box
    sx={{
      minWidth: 26,
      height: 28,
      borderRadius: 0.5,
      backgroundColor: 'error.main',
      color: '#fff',
      typography: 'textXS_bold',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 'none',
      fontSize: '8px',
      px: 0.5,
    }}
  >
    {docLabel(contentType)}
  </Box>
));

const PendingChip: FC = observer(() => {
  const { t } = useTrans();
  return (
    <Chip
      label={t('app.orgProfile.field.pendingSave')}
      size="small"
      sx={{
        height: 18,
        fontSize: '10px',
        backgroundColor: 'yellow.100',
        color: 'yellow.800',
        border: 1,
        borderColor: 'yellow.300',
        flex: 'none',
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
});

const ConfirmedChip: FC = observer(() => {
  const { t } = useTrans();
  return (
    <Chip
      label={t('app.orgProfile.field.confirmed')}
      size="small"
      sx={{
        height: 18,
        fontSize: '10px',
        backgroundColor: 'green.100',
        color: 'green.700',
        border: 1,
        borderColor: 'green.200',
        flex: 'none',
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
});

/**
 * Renderer for a `file`-type field.
 *
 * Value is `ProfileReferenceFileDto[]` — an array of pending + confirmed
 * descriptors returned by the active-template endpoint, or accumulated locally
 * while the user uploads before saving. Uploads go through the two-step
 * `/tenant-profile/references/*` flow (request URL → PUT → confirm); those
 * routes are unchanged and back the `file` field type.
 */
export const FileField: FC<FieldInputProps> = observer(
  ({ field, value, mode, dirty, disabled, onChange }) => {
    const { t } = useTrans();
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const requestUrl = useRequestReferenceUploadUrl();
    const confirmRef = useConfirmReferenceUpload();
    const discardRef = useDiscardReferenceUpload();

    const [uploading, setUploading] = useState(false);
    const [discardingId, setDiscardingId] = useState<string | null>(null);

    const validation = field.validation;
    const maxBytes: number = validation?.maxFileSizeBytes ?? GLOBAL_MAX_BYTES;
    const maxFiles: number = validation?.maxFiles ?? Infinity;
    const acceptedFormats: string[] = validation?.acceptedFormats ?? [];

    const files: ProfileReferenceFileDto[] = (
      Array.isArray(value) ? (value as ProfileReferenceFileDto[]) : []
    ).filter((f) => f.status !== 'deleted');

    const handleFileSelect = async (file: File) => {
      // Max files guard
      if (files.length >= maxFiles) {
        enqueueSnackbar(t('app.orgProfile.field.maxFilesReached', { count: maxFiles }), {
          variant: 'error',
        });
        return;
      }

      // File type guard
      if (acceptedFormats.length > 0) {
        const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
        if (!acceptedFormats.includes(ext)) {
          enqueueSnackbar(
            t('app.orgProfile.field.fileTypeNotAllowed', {
              formats: acceptedFormats.join(', '),
            }),
            { variant: 'error' },
          );
          return;
        }
      }

      // File size guard
      if (file.size > maxBytes) {
        enqueueSnackbar(
          t('app.orgProfile.field.fileTooLarge', {
            maxMb: (maxBytes / (1024 * 1024)).toFixed(0),
          }),
          { variant: 'error' },
        );
        return;
      }

      setUploading(true);
      try {
        const contentType = file.type || 'application/octet-stream';
        const { uploadUrl, documentId, s3Key } = await requestUrl.mutateAsync({
          fileName: file.name,
          contentType,
        });
        await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': contentType },
          body: file,
        });
        const refFile = await confirmRef.mutateAsync({
          fieldId: field.id,
          documentId,
          s3Key,
          fileName: file.name,
          contentType,
          fileSize: file.size,
        });
        onChange?.([...files, refFile]);
      } catch {
        enqueueSnackbar(t('app.orgProfile.field.uploadFailed'), { variant: 'error' });
      } finally {
        setUploading(false);
      }
    };

    const handleDiscard = async (docId: string) => {
      setDiscardingId(docId);
      try {
        await discardRef.mutateAsync(docId);
        onChange?.(files.filter((f) => f.documentId !== docId));
      } catch {
        enqueueSnackbar(t('app.orgProfile.field.discardFailed'), { variant: 'error' });
      } finally {
        setDiscardingId(null);
      }
    };

    const handleRemove = (docId: string) => {
      onChange?.(files.filter((f) => f.documentId !== docId));
    };

    // ── Read mode, empty ──────────────────────────────────────────────────
    if (mode === 'read' && files.length === 0) {
      return (
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'grey.400',
            borderRadius: `${ORG_TOKENS.radiusSm}px`,
            p: 1.75,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <FileIcon size={18} color={theme.palette.text.disabled} />
          <Typography variant="textXS_regular" color="text.tertiary" sx={{ fontStyle: 'italic' }}>
            {t('app.orgProfile.field.noArtifact')}
          </Typography>
        </Box>
      );
    }

    // ── Read mode, filled ─────────────────────────────────────────────────
    if (mode === 'read') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {files.map((f) => (
            <Box
              key={f.documentId}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}
            >
              <DocBadge contentType={f.contentType} />
              <Typography
                variant="textSM_medium"
                color="text.primary"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {f.fileName}
              </Typography>
              {f.status === 'pending' ? <PendingChip /> : <ConfirmedChip />}
            </Box>
          ))}
        </Box>
      );
    }

    // ── Edit mode ─────────────────────────────────────────────────────────
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {files.map((f) => (
          <Box
            key={f.documentId}
            sx={{
              ...editBoxSx(dirty && f.status === 'pending'),
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
            }}
          >
            <DocBadge contentType={f.contentType} />
            <Typography
              variant="textSM_regular"
              color="text.primary"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {f.fileName}
            </Typography>
            {f.status === 'pending' ? <PendingChip /> : <ConfirmedChip />}
            {f.status === 'pending' ? (
              <IconButton
                size="small"
                disabled={disabled || discardingId === f.documentId}
                onClick={() => void handleDiscard(f.documentId)}
                aria-label={t('app.orgProfile.field.discard')}
                sx={{ flex: 'none', color: 'text.tertiary', '&:hover': { color: 'error.main' } }}
              >
                {discardingId === f.documentId ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <XIcon size={14} />
                )}
              </IconButton>
            ) : (
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => handleRemove(f.documentId)}
                aria-label={t('app.orgProfile.field.unlink')}
                sx={{ flex: 'none', color: 'text.tertiary', '&:hover': { color: 'error.main' } }}
              >
                <TrashIcon size={14} />
              </IconButton>
            )}
          </Box>
        ))}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              accept={
                acceptedFormats.length > 0
                  ? acceptedFormats.join(',')
                  : '.pdf,.doc,.docx,.xlsx,.xls,.png,.jpeg,.jpg'
              }
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFileSelect(file);
                e.target.value = '';
              }}
            />
            <Button
              size="small"
              variant="secondary"
              disabled={disabled || uploading || files.length >= maxFiles}
              startIcon={
                uploading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <UploadSimpleIcon size={14} />
                )
              }
              onClick={() => fileInputRef.current?.click()}
              sx={{ textTransform: 'none' }}
            >
              {uploading
                ? t('app.orgProfile.field.uploading')
                : files.length === 0
                  ? t('app.orgProfile.field.uploadDocument')
                  : t('app.orgProfile.field.addAnotherDoc')}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="textXS_regular" color="text.disabled">
              {t('app.orgProfile.field.maxFileSize', {
                maxMb: (maxBytes / (1024 * 1024)).toFixed(0),
              })}
            </Typography>
            {acceptedFormats.length > 0 && (
              <Typography variant="textXS_regular" color="text.disabled">
                ·{' '}
                {t('app.orgProfile.field.acceptedFormats', {
                  formats: acceptedFormats.join(', '),
                })}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  },
);
